import {
  GraphEdge,
  GraphNode,
  NodeType
} from "modules/knowledge-library/data/models/knowledge-graph.model";
import { KnowledgeGraphResponse } from "../models/knowledge-graph.model";

const CARD_WIDTH = 230;

// Maps a free-text equipmentType to a NodeType category used for node coloring.
const mapEquipmentTypeToNodeType = (equipmentType: string): NodeType => {
  const value = (equipmentType || "").toLowerCase();
  if (
    /transmitter|indicator|controller|sensor|gauge|switch|analyzer/.test(value)
  ) {
    return NodeType.SENSOR;
  }
  if (/pipe|line|connector|fitting|flange|nozzle/.test(value)) {
    return NodeType.COMPONENT;
  }
  if (
    /valve|pump|exchanger|vessel|tank|compressor|drum|column|filter|separator|heater/.test(
      value
    )
  ) {
    return NodeType.EQUIPMENT;
  }
  return NodeType.COMPONENT;
};

// Arranges nodes on a circle since the API does not provide coordinates.
const circularLayout = (
  ids: string[]
): Record<string, { x: number; y: number }> => {
  const positions: Record<string, { x: number; y: number }> = {};
  const count = ids.length;
  if (count === 0) return positions;
  if (count === 1) {
    positions[ids[0]] = { x: 400, y: 300 };
    return positions;
  }
  const radius = Math.max(320, (count * (CARD_WIDTH + 60)) / (2 * Math.PI));
  const center = radius + 160;
  ids.forEach((id, index) => {
    const angle = (2 * Math.PI * index) / count - Math.PI / 2;
    positions[id] = {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle)
    };
  });
  return positions;
};

export interface MappedKnowledgeGraph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export const mapKnowledgeGraphResponse = (
  response: KnowledgeGraphResponse
): MappedKnowledgeGraph => {
  const rawNodes = response.nodes ?? [];
  const rawEdges = response.edges ?? [];

  const positions = circularLayout(rawNodes.map((node) => node.id));

  const nodes: GraphNode[] = rawNodes.map((node) => ({
    id: node.id,
    label: node.assetName,
    type: mapEquipmentTypeToNodeType(node.equipmentType),
    x: positions[node.id]?.x ?? 0,
    y: positions[node.id]?.y ?? 0
  }));

  const edges: GraphEdge[] = rawEdges.map((edge, index) => ({
    id: `${edge.source}__${edge.target}__${edge.relationshipType}__${index}`,
    source: edge.source,
    target: edge.target,
    relationship: edge.relationshipType
  }));

  return { nodes, edges };
};
